/**
 * ScreenshotTool — ⌘+Shift+4 (Mac) / Ctrl+Shift+4 area capture → RunFiles
 * Uses getDisplayMedia + canvas crop. Uploads to /api/files/upload.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Camera } from 'lucide-react'
import { API_URL, apiFetch } from '../../../config/api'

export default function ScreenshotTool() {
  const [selecting, setSelecting] = useState(false)
  const [frame, setFrame] = useState<ImageBitmap | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const startRef = useRef({ x: 0, y: 0 })
  const rectRef = useRef({ x: 0, y: 0, w: 0, h: 0 })

  const capture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true })
      const track = stream.getVideoTracks()[0]
      const imageCapture = new (window as any).ImageCapture(track)
      const bitmap = await imageCapture.grabFrame()
      track.stop()
      setFrame(bitmap)
      setSelecting(true)
    } catch {
      // user cancelled permission
    }
  }, [])

  // Global hotkey: ⌘+Shift+4 or Ctrl+Shift+4
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === '4') {
        e.preventDefault()
        capture()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [capture])

  // Draw frame to canvas when selecting
  useEffect(() => {
    if (!selecting || !frame || !canvasRef.current) return
    const c = canvasRef.current
    c.width = window.innerWidth
    c.height = window.innerHeight
    const ctx = c.getContext('2d')!
    ctx.drawImage(frame, 0, 0, c.width, c.height)
  }, [selecting, frame])

  const onMouseDown = (e: React.MouseEvent) => {
    startRef.current = { x: e.clientX, y: e.clientY }
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!e.buttons || !canvasRef.current || !frame) return
    const c = canvasRef.current
    const ctx = c.getContext('2d')!
    ctx.clearRect(0, 0, c.width, c.height)
    ctx.drawImage(frame, 0, 0, c.width, c.height)
    // dim overlay
    ctx.fillStyle = 'rgba(0,0,0,0.4)'
    ctx.fillRect(0, 0, c.width, c.height)
    // selection rect
    const x = Math.min(startRef.current.x, e.clientX)
    const y = Math.min(startRef.current.y, e.clientY)
    const w = Math.abs(e.clientX - startRef.current.x)
    const h = Math.abs(e.clientY - startRef.current.y)
    rectRef.current = { x, y, w, h }
    ctx.clearRect(x, y, w, h)
    ctx.drawImage(frame, x, y, w, h, x, y, w, h)
    ctx.strokeStyle = 'var(--os-accent, #818cf8)'
    ctx.lineWidth = 2
    ctx.strokeRect(x, y, w, h)
  }

  const onMouseUp = async () => {
    const { x, y, w, h } = rectRef.current
    if (w < 10 || h < 10 || !frame) { setSelecting(false); setFrame(null); return }

    // Crop
    const offscreen = document.createElement('canvas')
    const scaleX = frame.width / window.innerWidth
    const scaleY = frame.height / window.innerHeight
    offscreen.width = Math.round(w * scaleX)
    offscreen.height = Math.round(h * scaleY)
    const octx = offscreen.getContext('2d')!
    octx.drawImage(frame, x * scaleX, y * scaleY, offscreen.width, offscreen.height, 0, 0, offscreen.width, offscreen.height)

    setSelecting(false)
    setFrame(null)

    // Upload
    offscreen.toBlob(async (blob) => {
      if (!blob) return
      const ts = Date.now()
      const fd = new FormData()
      fd.append('file', blob, `screenshot_${ts}.png`)
      fd.append('folder', 'images')
      try {
        await apiFetch(`${API_URL}/api/files/upload`, { method: 'POST', body: fd })
        window.dispatchEvent(new CustomEvent('runai:toast', { detail: { type: 'success', message: 'Screenshot saved to RunFiles' } }))
      } catch {
        window.dispatchEvent(new CustomEvent('runai:toast', { detail: { type: 'error', message: 'Screenshot upload failed' } }))
      }
    }, 'image/png')
  }

  if (!selecting) return null

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 99999, cursor: 'crosshair' }}>
      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%', display: 'block' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
      />
      <div style={{
        position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
        background: 'var(--os-surface, rgba(22,27,34,0.88))',
        border: '1px solid var(--os-border, rgba(255,255,255,0.08))',
        borderRadius: 8, padding: '6px 14px',
        color: 'var(--os-text, #e6edf3)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <Camera size={14} /> Drag to select area • ESC to cancel
      </div>
    </div>
  )
}
